export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: Date;
}

export interface ChatResponse {
  message: string;
  suggestions?: string[];
  error?: string;
}

export interface ChatStreamEvent {
  event: string;
  data?: unknown;
}

export class ChatService {
  private authToken: string;

  constructor(authToken: string) {
    this.authToken = authToken;
  }

  async chat(
    _userId: string,
    message: string,
    conversationHistory: ChatMessage[] = [],
    options?: {
      formatInstructions?: string;
      onProgress?: (event: ChatStreamEvent) => void;
    }
  ): Promise<ChatResponse> {
    try {
      // Call the /api/chat endpoint - the API handles MCP calls internally
      const requestBody: {
        message: string;
        conversationHistory: Array<{
          role: ChatMessage['role'];
          content: string;
          timestamp?: string;
        }>;
        formatInstructions?: string;
        stream?: boolean;
      } = {
        message,
        conversationHistory: conversationHistory.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp?.toISOString(),
        })),
      };

      const trimmedInstructions = options?.formatInstructions?.trim();
      if (trimmedInstructions) {
        requestBody.formatInstructions = trimmedInstructions;
      }

      const shouldStream = typeof options?.onProgress === 'function';
      if (shouldStream) {
        requestBody.stream = true;
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: shouldStream ? 'text/event-stream' : 'application/json',
          Authorization: `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(
          `Chat API error: ${response.status} ${response.statusText}`
        );
      }

      if (shouldStream && response.body && options?.onProgress) {
        return this.handleStreamResponse(response, options.onProgress);
      }

      const chatResponse: {
        success: boolean;
        message?: string;
        suggestions?: string[];
        error?: string;
      } = await response.json();

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Chat API request failed');
      }

      return {
        message:
          chatResponse.message ||
          'I apologize, but I could not generate a response.',
        suggestions: chatResponse.suggestions?.slice(0, 3) || [], // Ensure max 3
      };
    } catch (error) {
      console.error('Error in chat service:', error);

      // Handle API errors with user-friendly messages
      return {
        message:
          'I apologize, but I encountered an error while processing your request. Please try again later.',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Method to update auth token (useful for token refresh)
  updateAuthToken(token: string) {
    this.authToken = token;
  }

  private async handleStreamResponse(
    response: Response,
    onProgress: (event: ChatStreamEvent) => void
  ): Promise<ChatResponse> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Streaming response is not supported in this browser');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let completion: ChatResponse | null = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        if (rawEvent.trim().length === 0) {
          boundary = buffer.indexOf('\n\n');
          continue;
        }

        const { event, data } = this.parseSseChunk(rawEvent);
        onProgress({ event, data });

        if (event === 'completion') {
          completion = (data || null) as ChatResponse;
        } else if (event === 'error') {
          throw new Error(
            typeof data === 'string'
              ? data
              : (data as { message?: string })?.message || 'Streaming error'
          );
        }

        boundary = buffer.indexOf('\n\n');
      }
    }

    if (!completion) {
      throw new Error('Streaming response did not include a completion event');
    }

    return completion;
  }

  private parseSseChunk(chunk: string): { event: string; data?: unknown } {
    const lines = chunk.split('\n');
    let eventName = 'message';
    let dataPayload = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataPayload += line.slice(5).trim();
      }
    }

    let parsed: unknown = dataPayload;
    if (dataPayload) {
      try {
        parsed = JSON.parse(dataPayload);
      } catch {
        parsed = dataPayload;
      }
    }

    return { event: eventName, data: parsed };
  }
}
